/**
 * SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/Workflows/CreateWorkflow.tsx
 * PURPOSE: Sprint 11 Create New Workflow Instance Form
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 2: Milestone-Gated Roadmaps
 *
 * APPROVED: 2025-12-02 by CTO (Stage 02 Design Document S11-FE-DESIGN-001)
 * COMPLIANCE: SDLC 6.3.1 + Multi-Industry BPM + NQH Brand Design System V2.0
 * CTO MANDATE: Form for creating new workflow instances from templates
 *
 * @business-context
 * - Domain: Multi-Industry BPM Instance Creation
 * - Impact: Operational workflow initiation for F&B and Cleaning
 * - Stakeholders: Supervisors, Managers, System Operators
 *
 * @technical-architecture
 * - Layer: Presentation Layer
 * - Pattern: React Form Component with Template Selection
 * - Dependencies: React 18, TypeScript, workflowService
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/
 * - API: docs/03-integrate/01-API-Specifications/
 *  */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  workflowService,
  WorkflowTemplate,
  CreateWorkflowInstanceRequest,
  getIndustryLabel,
  INDUSTRY_LABELS,
  IndustryCode,
} from '../../services/api/workflowService';

// ============= Component Types =============

interface FormData {
  template_id: string;
  scheduled_start: string;
  scheduled_end: string;
  instance_metadata: Record<string, string>;
}

// ============= Create Workflow Component =============

export const CreateWorkflow: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedTemplateId = searchParams.get('template');

  // State
  const [templates, setTemplates] = useState<WorkflowTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<WorkflowTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [industryFilter, setIndustryFilter] = useState<IndustryCode | 'all'>('all');

  const [formData, setFormData] = useState<FormData>({
    template_id: '',
    scheduled_start: '',
    scheduled_end: '',
    instance_metadata: {},
  });

  // Fetch templates
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const data = await workflowService.getActiveTemplates();
        setTemplates(data);

        // If preselected template, select it
        if (preselectedTemplateId) {
          const template = data.find((t) => t.id === preselectedTemplateId);
          if (template) {
            setSelectedTemplate(template);
            setFormData((prev) => ({ ...prev, template_id: template.id }));
            setIndustryFilter(template.industry);
          }
        }
      } catch (err) {
        console.error('Failed to fetch templates:', err);
        setError('Không thể tải danh sách templates.');
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, [preselectedTemplateId]);

  // Filter templates by industry
  const filteredTemplates = templates.filter(
    (t) => industryFilter === 'all' || t.industry === industryFilter
  );

  // Handle template selection
  const handleTemplateSelect = (template: WorkflowTemplate) => {
    setSelectedTemplate(template);
    setFormData((prev) => ({ ...prev, template_id: template.id }));
  };

  // Handle metadata field change
  const handleMetadataChange = (key: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      instance_metadata: {
        ...prev.instance_metadata,
        [key]: value,
      },
    }));
  };

  // Handle form submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedTemplate) {
      setError('Vui lòng chọn một template.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const request: CreateWorkflowInstanceRequest = {
        template_id: formData.template_id,
        scheduled_start: formData.scheduled_start || undefined,
        scheduled_end: formData.scheduled_end || undefined,
        metadata: formData.instance_metadata,
      };

      const instance = await workflowService.createInstance(request);
      navigate(`/workflows/instances/${instance.id}`);
    } catch (err) {
      console.error('Failed to create workflow:', err);
      setError('Không thể tạo workflow. Vui lòng thử lại.');
    } finally {
      setSubmitting(false);
    }
  };

  // Get required fields for assignment state
  const getRequiredFields = (): string[] => {
    if (!selectedTemplate?.state_config?.ASSIGNMENT) return [];
    return selectedTemplate.state_config.ASSIGNMENT.required_fields || [];
  };

  // Render loading
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Đang tải templates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/workflows')}
            className="text-blue-600 hover:text-blue-800 text-sm mb-2 flex items-center"
          >
            ← Quay lại Dashboard
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Tạo Workflow mới</h1>
          <p className="text-sm text-gray-500 mt-1">
            Chọn template và điền thông tin để tạo workflow mới
          </p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
            <button onClick={() => setError(null)} className="ml-4 underline">
              Đóng
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Step 1: Select Template */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">1. Chọn Template</h2>

            {/* Industry Filter */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Lọc theo ngành
              </label>
              <select
                value={industryFilter}
                onChange={(e) => setIndustryFilter(e.target.value as IndustryCode | 'all')}
                className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Tất cả ngành</option>
                {Object.entries(INDUSTRY_LABELS).map(([code, labels]) => (
                  <option key={code} value={code}>
                    {labels.vi}
                  </option>
                ))}
              </select>
            </div>

            {/* Template Grid */}
            {filteredTemplates.length === 0 ? (
              <p className="text-gray-500 text-center py-4">Không có template nào</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredTemplates.map((template) => (
                  <TemplateSelectCard
                    key={template.id}
                    template={template}
                    isSelected={selectedTemplate?.id === template.id}
                    onClick={() => handleTemplateSelect(template)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Step 2: Fill Details */}
          {selectedTemplate && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">2. Thông tin Workflow</h2>

              <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Template đã chọn:</strong> {selectedTemplate.display_name_vi}
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  {getIndustryLabel(selectedTemplate.industry)} • {selectedTemplate.bcc_prefix}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Scheduled Start */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Thời gian bắt đầu dự kiến
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.scheduled_start}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, scheduled_start: e.target.value }))
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Scheduled End */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Thời gian kết thúc dự kiến
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.scheduled_end}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, scheduled_end: e.target.value }))
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Required Fields from Template */}
              {getRequiredFields().length > 0 && (
                <div className="mt-6">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">
                    Thông tin bắt buộc cho trạng thái Phân công
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {getRequiredFields().map((field) => (
                      <div key={field}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {formatFieldLabel(field)} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type={getFieldType(field)}
                          value={formData.instance_metadata[field] || ''}
                          onChange={(e) => handleMetadataChange(field, e.target.value)}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Submit Button */}
          {selectedTemplate && (
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => navigate('/workflows')}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Hủy
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {submitting ? 'Đang tạo...' : 'Tạo Workflow'}
              </button>
            </div>
          )}
        </form>
      </main>
    </div>
  );
};

// ============= Sub Components =============

interface TemplateSelectCardProps {
  template: WorkflowTemplate;
  isSelected: boolean;
  onClick: () => void;
}

const TemplateSelectCard: React.FC<TemplateSelectCardProps> = ({
  template,
  isSelected,
  onClick,
}) => {
  const industryIcons: Record<IndustryCode, string> = {
    FB: '🍽️',
    CL: '🧹',
    NR: '👩‍⚕️',
    HC: '🏥',
    HT: '🏨',
  };

  return (
    <div
      onClick={onClick}
      className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
        isSelected
          ? 'border-blue-500 bg-blue-50'
          : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
      }`}
    >
      <div className="flex items-start justify-between">
        <span className="text-2xl">{industryIcons[template.industry]}</span>
        {isSelected && (
          <span className="px-2 py-0.5 bg-blue-500 text-white text-xs rounded">Đã chọn</span>
        )}
      </div>
      <h3 className="font-medium mt-2">{template.display_name_vi || template.display_name}</h3>
      <p className="text-sm text-gray-500 mt-1 line-clamp-2">{template.description}</p>
      <div className="mt-3 flex items-center gap-2">
        <span className="text-xs px-2 py-0.5 bg-gray-100 rounded">{template.bcc_prefix}</span>
        <span className="text-xs text-gray-400">{getIndustryLabel(template.industry)}</span>
      </div>
    </div>
  );
};

// ============= Helper Functions =============

function formatFieldLabel(field: string): string {
  const labels: Record<string, string> = {
    employee_id: 'Mã nhân viên',
    shift_code: 'Mã ca',
    scheduled_time: 'Thời gian',
    table_number: 'Số bàn',
    recipe_id: 'Mã món',
    quantity: 'Số lượng',
    station: 'Station',
    site_id: 'Mã site',
    inspector_id: 'Mã thanh tra',
    storage_location: 'Vị trí kho',
  };

  return labels[field] || field.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
}

function getFieldType(field: string): string {
  if (field.includes('time') || field.includes('date')) return 'datetime-local';
  if (field.includes('quantity') || field.includes('number')) return 'number';
  return 'text';
}

export default CreateWorkflow;
